import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { AlertMessageComponent } from 'app/components/ui-kit/alerts/alert-message/alert-message.component';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { BaseInputComponent } from 'app/components/ui-kit/inputs/base-input/base-input.component';
import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';
import { SplitPageWrapperComponent } from 'app/components/ui-kit/page-components/split-page-wrapper/split-page-wrapper.component';
import { AppStateService } from 'app/services/app-state.service';
import { AuthService } from 'app/services/auth.service';
import { ThemeService } from 'app/services/theme.service';
import { UserService } from 'app/services/user.service';
import { environment } from 'env/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    SvgLoaderComponent,
    SplitPageWrapperComponent,
    BaseInputComponent,
    PrimaryBtnComponent,
    AlertMessageComponent,
    SpinnerComponent,
    RouterModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  email = '';
  password = '';
  errorMessage = '';
  currentTheme: string = '';
  ctaText: string = ' ';

  isAuthenticating: boolean = false;
  isAuthenticatingGoogle: boolean = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private themeService: ThemeService,
    private userService: UserService,
    private appState: AppStateService,
  ) {
    const theme = this.themeService.getCurrentTheme();
    this.currentTheme = theme;
  }

  ngAfterViewInit() {
    this.typeOutText('Your AI Assistant for Efficient Private Travel');
  }

  async signIn() {
    this.isAuthenticating = true;
    this.errorMessage = null;
    try {
      await this.authService.signIn(this.email, this.password);
      const user = await this.userService.getUserProfile();
      this.appState.user.profile.set(user);
      this.redirectAfterLogin();
    } catch (error) {
      // alert(`Sign-in failed: ${error.message}`);
      this.errorMessage = `Invalid Email and/or Password`;
      this.isAuthenticating = false;
    }
  }

  async signInWithGoogle() {
    this.isAuthenticatingGoogle = true;
    this.errorMessage = '';

    try {
      // ← Calls only the login-blocking variant
      await this.authService.signInWithGoogle();

      // Fetch your Firestore profile (must exist)
      const profile = await this.userService.getUserProfile();
      this.appState.user.profile.set(profile!);

      this.redirectAfterLogin();
    } catch (err: any) {
      if (err.message === 'no-account') {
        this.errorMessage = 'No account found. Sign Up to Continue.';
      } else {
        // your existing mapping of other errors...
        this.errorMessage = this.formatAuthError(err);
      }
      this.isAuthenticatingGoogle = false;
    }
  }

  // TODO: MOVE CODE TO THE NEW ROUTE
  async resetPassword() {
    try {
      await this.authService.forgotPassword(this.email);
      alert('Password reset email sent!');
    } catch (error) {
      alert(`Reset failed: ${error.message}`);
    }
  }

  private redirectAfterLogin() {
    setTimeout(() => {
      const returnUrl =
        this.route.snapshot.queryParams['returnUrl'] || '/initialize';
      this.router.navigateByUrl(returnUrl);
      this.errorMessage = '';
      this.isAuthenticatingGoogle = false;
      this.isAuthenticating = false;
    }, 1200);
  }

  typeOutText(fullText: string, speed: number = 20): void {
    this.ctaText = ' '; // reset before typing
    let index = 0;

    const interval = setInterval(() => {
      this.ctaText += fullText.charAt(index);
      index++;

      if (index >= fullText.length) {
        clearInterval(interval);
      }
    }, speed);
  }

  formatAuthError(error: any): string {
    const raw = typeof error === 'string' ? error : error?.message || '';

    // Remove "Firebase: Error" prefix and parentheses
    const cleaned = raw
      .replace(/^Firebase:\s*/i, '')
      .replace(/^Error\s*/i, '')
      .replace(/[\(\)]/g, '')
      .replace(/auth\//, '')
      .trim();

    // Optionally map known error codes to friendly messages
    const friendlyMap: Record<string, string> = {
      'popup-closed-by-user': 'Google popup was closed before completing.',
      'user-not-found': 'No user found with that email.',
      'wrong-password': 'Incorrect password.',
      'email-already-in-use': 'Email is already registered.',
      'popup-blocked': 'Google Popup was blocked by the browser.',
      'network-request-failed': 'Network error. Please check your connection.',
      'too-many-requests': 'Too many attempts. Try again later.',
    };

    const lower = cleaned.toLowerCase();
    for (const code in friendlyMap) {
      if (lower.includes(code)) {
        return friendlyMap[code];
      }
    }

    return 'An unexpected error occurred. Please try again.';
  }
}
